export const parseNumeric = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const CONTRACT_STATUSES = Object.freeze(["draft", "active", "fulfilled", "expired", "terminated", "canceled"]);
export const BILLING_PLAN_TYPES = Object.freeze(["one_time", "recurring", "subscription", "installment", "milestone"]);
export const BILLING_PLAN_STATUSES = Object.freeze(["draft", "active", "paused", "completed", "canceled"]);
export const BILLING_FREQUENCIES = Object.freeze(["one_time", "weekly", "biweekly", "monthly", "quarterly", "yearly"]);
export const PAYMENT_STATUSES = Object.freeze(["pending", "partial", "paid", "overdue", "canceled"]);
export const PAYMENT_METHODS = Object.freeze(["transfer", "cash", "card"]);
export const CONTRACT_DETAILS_TABS = Object.freeze(["overview", "onboarding", "billing", "activity", "files", "history", "mailjet"]);

const formatPart = (value) => String(value).padStart(2, "0");

export const parseDateTimeLikeParts = (value) => {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
      hour: value.getHours(),
      minute: value.getMinutes(),
      second: value.getSeconds(),
    };
  }

  const normalized = String(value || "").trim();
  if (!normalized) return null;

  const directMatch = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (directMatch) {
    return {
      year: Number(directMatch[1]),
      month: Number(directMatch[2]),
      day: Number(directMatch[3]),
      hour: Number(directMatch[4] || "0"),
      minute: Number(directMatch[5] || "0"),
      second: Number(directMatch[6] || "0"),
    };
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  return {
    year: parsed.getFullYear(),
    month: parsed.getMonth() + 1,
    day: parsed.getDate(),
    hour: parsed.getHours(),
    minute: parsed.getMinutes(),
    second: parsed.getSeconds(),
  };
};

export const parseDateLikeParts = (value) => {
  const parts = parseDateTimeLikeParts(value);
  if (!parts) return null;
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
  };
};

export const buildLocalDate = (parts) => {
  if (!parts) return null;
  const date = new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour || 0),
    Number(parts.minute || 0),
    Number(parts.second || 0)
  );
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDisplayDateParts = (parts) => {
  if (!parts) return "-";
  return `${formatPart(parts.day)}.${formatPart(parts.month)}.${String(parts.year).padStart(4, "0")}`;
};

export const formatDisplayDateTimeParts = (parts) => {
  if (!parts) return "-";
  return `${formatDisplayDateParts(parts)}, ${formatPart(parts.hour)}:${formatPart(parts.minute)}`;
};

export const toDateInputValue = (value) => {
  const parts = parseDateLikeParts(value);
  if (!parts) return "";
  return `${String(parts.year).padStart(4, "0")}-${formatPart(parts.month)}-${formatPart(parts.day)}`;
};

export const toDateTimeInputValue = (value) => {
  const parts = parseDateTimeLikeParts(value);
  if (!parts) return "";
  return `${String(parts.year).padStart(4, "0")}-${formatPart(parts.month)}-${formatPart(parts.day)}T${formatPart(parts.hour)}:${formatPart(parts.minute)}`;
};

export const currentDateTimeInputValue = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export const fromDateInputValue = (value) => {
  const normalized = String(value || "").trim();
  return normalized || null;
};

export const fromDateTimeInputValue = (value) => {
  const normalized = String(value || "").trim();
  return normalized || null;
};

export const normalizeMoneyInput = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  const parsed = Number(normalized.replace(",", "."));
  if (!Number.isFinite(parsed)) return null;
  return Math.round(parsed * 100) / 100;
};

export const calculatePaymentRemainingAmount = (payment = null) => {
  if (!payment) return 0;
  const amountDue = normalizeMoneyInput(payment?.amount_due) ?? 0;
  const amountPaid = normalizeMoneyInput(payment?.amount_paid) ?? 0;
  return Math.max(0, Math.round((amountDue - amountPaid) * 100) / 100);
};

export const isPaymentCollectionRecordable = (payment = null) => {
  if (!payment) return false;
  const status = normalizeEnumValue(payment?.status, PAYMENT_STATUSES, "");
  if (["paid", "canceled"].includes(status)) return false;
  return calculatePaymentRemainingAmount(payment) > 0;
};

export const normalizeIntegerInput = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;
  return Math.trunc(parsed);
};

export const isFilledValue = (value) => String(value ?? "").trim().length > 0;

export const normalizeEnumValue = (value, allowedValues, fallback = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return fallback;
  return allowedValues.includes(normalized) ? normalized : fallback;
};

export const normalizeBillingPlanTypeValue = (value, fallback = "one_time") =>
  normalizeEnumValue(value, BILLING_PLAN_TYPES, fallback);

export const normalizeBillingFrequencyValue = (value, fallback = "") =>
  normalizeEnumValue(value, BILLING_FREQUENCIES, fallback);

export const toMoneyInputValue = (value) => {
  const normalized = normalizeMoneyInput(value);
  if (normalized === null) return "";
  return normalized.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
};

export const extractTimePartFromDateTimeInputValue = (value, fallback = "09:00") => {
  const normalized = String(value || "").trim();
  const match = normalized.match(/T(\d{2}:\d{2})/);
  return match?.[1] || fallback;
};

export const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

export const parseDateParts = (value) => {
  const parts = parseDateLikeParts(value);
  if (!parts) return null;
  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (month < 1 || month > 12) return null;
  const maxDay = daysInMonth(year, month);
  if (day < 1 || day > maxDay) return null;
  return { year, month, day };
};

export const formatDateParts = (parts) => {
  if (!parts) return "";
  return `${String(parts.year).padStart(4, "0")}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
};

export const compareDateParts = (left, right) => {
  if (left.year !== right.year) return left.year - right.year;
  if (left.month !== right.month) return left.month - right.month;
  return left.day - right.day;
};

export const addDaysToDateParts = (parts, amount) => {
  const date = new Date(parts.year, parts.month - 1, parts.day + amount);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
};

export const addMonthsToDateParts = (parts, amount, preferredDay = parts.day) => {
  const monthIndex = (parts.month - 1) + amount;
  const year = parts.year + Math.floor(monthIndex / 12);
  const month = ((monthIndex % 12) + 12) % 12 + 1;
  return {
    year,
    month,
    day: Math.min(preferredDay, daysInMonth(year, month)),
  };
};

export const resolveNextDueDateTimeInputValue = ({
  planType = "one_time",
  billingFrequency = "",
  startDate = "",
  billingDayOfMonth = null,
  currentNextDueAt = "",
}) => {
  const startParts = parseDateParts(startDate);
  if (!startParts) return "";

  const normalizedPlanType = normalizeBillingPlanTypeValue(planType, "one_time");
  const normalizedFrequency = normalizeBillingFrequencyValue(billingFrequency, "");
  const timePart = extractTimePartFromDateTimeInputValue(currentNextDueAt, "09:00");
  let dueParts = startParts;

  if (normalizedPlanType === "one_time" || normalizedPlanType === "milestone" || normalizedFrequency === "one_time") {
    return `${formatDateParts(dueParts)}T${timePart}`;
  }

  if (normalizedFrequency === "weekly") {
    return `${formatDateParts(dueParts)}T${timePart}`;
  }
  if (normalizedFrequency === "biweekly") {
    return `${formatDateParts(dueParts)}T${timePart}`;
  }

  if (["monthly", "quarterly", "yearly"].includes(normalizedFrequency)) {
    const preferredDay = Math.min(
      Math.max(parseNumeric(billingDayOfMonth, startParts.day) || startParts.day, 1),
      31
    );
    dueParts = {
      year: startParts.year,
      month: startParts.month,
      day: Math.min(preferredDay, daysInMonth(startParts.year, startParts.month)),
    };
    if (compareDateParts(dueParts, startParts) < 0) {
      const monthsToAdd = normalizedFrequency === "monthly" ? 1 : normalizedFrequency === "quarterly" ? 3 : 12;
      dueParts = addMonthsToDateParts(startParts, monthsToAdd, preferredDay);
    }
    return `${formatDateParts(dueParts)}T${timePart}`;
  }

  return `${formatDateParts(addDaysToDateParts(startParts, 0))}T${timePart}`;
};

// Contract attachments are no longer served from the legacy public file mount
// (DPIA action A1 — docs/registration/legal/dpia-crm.md, risk R4). A direct
// stored-path link made contract files readable without token or tenant check.
//
// The file is fetched from an authenticated backend route instead. The returned
// value is a path relative to the axios base URL, so the request carries the
// Authorization header like any other API call — which also means it cannot be
// handed to `window.open` directly; the caller fetches a blob first.
export const buildContractFileDownloadPath = (contractId, fileId) => {
  const normalizedContractId = Number(contractId);
  const normalizedFileId = Number(fileId);
  if (!Number.isFinite(normalizedContractId) || normalizedContractId <= 0) return null;
  if (!Number.isFinite(normalizedFileId) || normalizedFileId <= 0) return null;
  return `/ncrm/contracts/${normalizedContractId}/files/${normalizedFileId}/download`;
};
